/**
 * Pivot Confluence
 *
 * Plots multiple pivot point systems (Standard, Fibonacci, Woodie, Camarilla)
 * on a single overlay to identify confluence zones where pivots cluster.
 *
 * Category: Support/Resistance | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const pivotConfluenceIndicator: IndicatorDefinition = {
  id: "pivot_confluence",
  name: "Pivot Confluence",
  shortName: "PivCnf",
  category: "support_resistance",
  pane: "overlay",
  params: [],
  plots: [
    { key: "pivot", label: "Pivot", color: "#FFD600", style: "line", lineWidth: 2 },
    { key: "r1", label: "R1 Avg", color: "#F44336", style: "line", lineWidth: 1 },
    { key: "s1", label: "S1 Avg", color: "#4CAF50", style: "line", lineWidth: 1 },
    { key: "r2", label: "R2 Avg", color: "#E57373", style: "line", lineWidth: 1 },
    { key: "s2", label: "S2 Avg", color: "#81C784", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const pivotData: { time: number; value: number }[] = [];
    const r1Data: { time: number; value: number }[] = [];
    const s1Data: { time: number; value: number }[] = [];
    const r2Data: { time: number; value: number }[] = [];
    const s2Data: { time: number; value: number }[] = [];

    for (let i = 1; i < candles.length; i++) {
      const prev = candles[i - 1];
      const h = prev.high, l = prev.low, c = prev.close;
      const pp = (h + l + c) / 3;

      // Standard pivots
      const stdR1 = 2 * pp - l;
      const stdS1 = 2 * pp - h;
      const stdR2 = pp + (h - l);
      const stdS2 = pp - (h - l);

      // Fibonacci pivots
      const fibR1 = pp + 0.382 * (h - l);
      const fibS1 = pp - 0.382 * (h - l);
      const fibR2 = pp + 0.618 * (h - l);
      const fibS2 = pp - 0.618 * (h - l);

      // Average confluence
      pivotData.push({ time: candles[i].time, value: pp });
      r1Data.push({ time: candles[i].time, value: (stdR1 + fibR1) / 2 });
      s1Data.push({ time: candles[i].time, value: (stdS1 + fibS1) / 2 });
      r2Data.push({ time: candles[i].time, value: (stdR2 + fibR2) / 2 });
      s2Data.push({ time: candles[i].time, value: (stdS2 + fibS2) / 2 });
    }

    return [
      { key: "pivot", data: pivotData },
      { key: "r1", data: r1Data },
      { key: "s1", data: s1Data },
      { key: "r2", data: r2Data },
      { key: "s2", data: s2Data },
    ];
  },
};
